import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RegistroCta } from '@/features/auth/components/registro-cta';
import { LandingNavbar } from '@/features/landing/components/landing-navbar';
import { ChevronRight, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { docsNavigation } from '../data/docs-navigation';

interface DocsLayoutProps {
  children: React.ReactNode;
  title: string;
  description?: string;
}

export function DocsLayout({ children, title, description }: DocsLayoutProps) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentNav = docsNavigation.find((item) => item.href === location.pathname);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <LandingNavbar />

      {/* Spacer for fixed navbar */}
      <div className="h-16" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1.5 py-4 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">
            Inicio
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link
            to="/docs/product-guide"
            className="hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            Documentacion
          </Link>
          {currentNav && (
            <>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-gray-900 dark:text-white font-medium">{currentNav.label}</span>
            </>
          )}
        </nav>

        <div className="flex gap-8 pb-16">
          {/* Mobile sidebar toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="fixed bottom-6 right-6 z-40 lg:hidden w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-lg flex items-center justify-center"
            aria-label="Abrir menu de documentacion"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Sidebar */}
          <aside
            className={cn(
              'fixed inset-y-0 left-0 z-30 w-72 bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 pt-20 px-4 pb-8 transition-transform duration-300 lg:relative lg:inset-auto lg:z-auto lg:w-64 lg:shrink-0 lg:border-0 lg:pt-0 lg:px-0 lg:translate-x-0 lg:bg-transparent',
              sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            )}
          >
            {/* Mobile overlay */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 bg-black/50 z-[-1] lg:hidden"
                onClick={() => setSidebarOpen(false)}
              />
            )}

            <div className="sticky top-20">
              <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 px-3">
                Documentacion
              </h3>
              <nav className="space-y-1">
                {docsNavigation.map((item, idx) => {
                  if (item.separator) {
                    return (
                      <div
                        key={`sep-${idx}`}
                        className="my-3 border-t border-gray-200 dark:border-gray-800"
                      />
                    );
                  }

                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                        isActive
                          ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 font-medium'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-900'
                      )}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>

              {/* CTA */}
              <div className="mt-8 mx-3 p-4 rounded-xl bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border border-blue-100 dark:border-blue-900/50">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Listo para comenzar?
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Crea tu cuenta gratuita y empieza a optimizar tu pricing.
                </p>
                <RegistroCta
                  className="mt-3 inline-block text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-2 rounded-lg hover:shadow-md transition-shadow"
                >
                  Crear Cuenta Gratis
                </RegistroCta>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main className="min-w-0 flex-1">
            {/* Page header */}
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                {title}
              </h1>
              {description && (
                <p className="mt-2 text-gray-500 dark:text-gray-400 text-base sm:text-lg">
                  {description}
                </p>
              )}
            </div>

            {children}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              &copy; {new Date().getFullYear()} Prixia. Todos los derechos reservados.
            </p>
            <nav className="flex items-center gap-6">
              <Link
                to="/terms"
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Terminos
              </Link>
              <Link
                to="/privacy"
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Privacidad
              </Link>
              <Link
                to="/contract"
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Contrato SaaS
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
